/*
 * Generic N-axis radar — one polygon "blob" per entity overlaid on a shared
 * grid, against a fixed 100% radius. Used by the shot-type pentagon and the
 * positioning hexagons (team formation, player court zones).
 *
 * A single entity shows the per-vertex % and no legend; several entities show
 * a color legend (with avatars) and no per-vertex numbers. An optional pair of
 * watermark icons marks a top (forward / aggressive) and bottom (back) zone.
 */

import type React from 'react';
import { EntityAvatar } from './EntityAvatar';

type AxisIcon = React.ComponentType<{
  size?: string | number;
  strokeWidth?: string | number;
  className?: string;
}>;

export interface RadarSeries {
  id: string;
  name: string;
  color: string;
  crop?: string;
  crops?: string[];
  /** One fraction (0..1) per axis, aligned to `axes`. */
  values: number[];
}

export interface RadarAxis {
  label: string;
  /** Optional small glyph shown beside the axis label. */
  Icon?: AxisIcon;
}

// Seven axes put two labels almost horizontal (±13° off the equator), where
// the outward-anchored text needs the most room. Rather than widening the
// 430px report — or clipping the labels on a 320px phone — a 7+ axis radar
// pulls its ring in and switches to the denser label type below.
const DENSE_FROM_AXES = 7;
const RINGS = [0.25, 0.5, 0.75, 1];

const geometry = (n: number) => {
  const dense = n >= DENSE_FROM_AXES;
  const size = dense ? 208 : 252;
  const r = dense ? 66 : 90;
  return { dense, size, center: size / 2, r, labelR: r + 12 };
};

const Radar: React.FC<{
  axes: RadarAxis[];
  data: RadarSeries[];
  topIcon?: AxisIcon;
  bottomIcon?: AxisIcon;
  /** Degrees to turn the whole wheel by, so a caller can choose whether an
      axis points straight up or a PAIR of them straddles the top. */
  rotate?: number;
}> = ({
  axes,
  data,
  topIcon: TopIcon,
  bottomIcon: BottomIcon,
  rotate = 0,
}) => {
  const n = axes.length;
  const single = data.length === 1;
  const {
    dense,
    size: SIZE,
    center: CENTER,
    r: R,
    labelR: LABEL_R,
  } = geometry(n);

  const angleFor = (i: number) =>
    ((-90 + rotate + (i * 360) / n) * Math.PI) / 180;
  const point = (i: number, frac: number): [number, number] => {
    const a = angleFor(i);
    return [CENTER + R * frac * Math.cos(a), CENTER + R * frac * Math.sin(a)];
  };
  const toPath = (fracs: number[]) =>
    fracs.map((f, i) => point(i, f).join(',')).join(' ');

  // Anchor each label outward from its vertex so text never crosses the chart.
  const labelAnchor = (a: number): React.CSSProperties => {
    const cos = Math.cos(a);
    if (Math.abs(cos) < 0.3) {
      return {
        transform:
          Math.sin(a) < 0 ? 'translate(-50%, -100%)' : 'translate(-50%, 0)',
      };
    }
    return {
      transform: cos > 0 ? 'translate(0, -50%)' : 'translate(-100%, -50%)',
    };
  };

  return (
    <div className={dense ? 'radar radar--dense' : 'radar'}>
      <div className="radar-stage" style={{ height: SIZE }}>
        <svg
          className="radar-svg"
          width={SIZE}
          height={SIZE}
          viewBox={`0 0 ${SIZE} ${SIZE}`}
        >
          {/* grid rings */}
          {RINGS.map((lvl) => (
            <polygon
              key={lvl}
              points={toPath(axes.map(() => lvl))}
              fill={lvl === 1 ? 'var(--hairline-2)' : 'none'}
              stroke="var(--hairline)"
              strokeWidth={1}
            />
          ))}
          {/* spokes */}
          {axes.map((_, i) => {
            const [x, y] = point(i, 1);
            return (
              <line
                key={i}
                x1={CENTER}
                y1={CENTER}
                x2={x}
                y2={y}
                stroke="var(--hairline)"
                strokeWidth={1}
              />
            );
          })}
          {/* zone watermarks — top (forward) / bottom (back), behind blobs */}
          {TopIcon ? (
            <g
              transform={`translate(${CENTER - 11}, ${CENTER - R * 0.62 - 11})`}
              opacity={0.16}
              style={{ color: 'var(--ink)' }}
            >
              <TopIcon size={22} strokeWidth={2} />
            </g>
          ) : null}
          {BottomIcon ? (
            <g
              transform={`translate(${CENTER - 10}, ${CENTER + R * 0.58 - 10})`}
              opacity={0.16}
              style={{ color: 'var(--ink)' }}
            >
              <BottomIcon size={20} strokeWidth={2} />
            </g>
          ) : null}
          {/* blobs — one translucent polygon per entity */}
          {data.map((d) => (
            <polygon
              key={d.id}
              points={toPath(d.values.map((v) => v))}
              fill={d.color}
              fillOpacity={single ? 0.22 : 0.16}
              stroke={d.color}
              strokeWidth={2}
              strokeLinejoin="round"
              style={{ transition: 'all 500ms cubic-bezier(0.5, 0.1, 0.2, 1)' }}
            />
          ))}
          {/* vertex dots */}
          {data.map((d) =>
            d.values.map((v, i) => {
              const [x, y] = point(i, v);
              return (
                <circle
                  key={`${d.id}-${i}`}
                  cx={x}
                  cy={y}
                  r={3}
                  fill={d.color}
                />
              );
            }),
          )}
        </svg>

        {/* labels — HTML overlay, anchored outward from each vertex */}
        {axes.map((ax, i) => {
          const a = angleFor(i);
          const lx = CENTER + LABEL_R * Math.cos(a);
          const ly = CENTER + LABEL_R * Math.sin(a);
          const Icon = ax.Icon;
          return (
            <div
              key={i}
              className="radar-label"
              style={{
                left: `calc(50% + ${lx - CENTER}px)`,
                top: ly,
                ...labelAnchor(a),
              }}
            >
              <span className="radar-label-head">
                {Icon ? (
                  <Icon
                    className="radar-label-icon"
                    size={13}
                    strokeWidth={2.2}
                  />
                ) : null}
                <span className="radar-label-name">{ax.label}</span>
              </span>
              {single ? (
                <span
                  className="radar-label-val"
                  style={{ color: data[0].color }}
                >
                  {Math.round(data[0].values[i] * 100)}%
                </span>
              ) : null}
            </div>
          );
        })}
      </div>

      {/* color legend — only when comparing */}
      {!single ? (
        <div className="radar-legend">
          {data.map((d) => (
            <span key={d.id} className="radar-legend-item">
              <EntityAvatar
                name={d.name}
                color={d.color}
                crop={d.crop}
                crops={d.crops}
              />
              <span className="radar-legend-name">{d.name}</span>
            </span>
          ))}
        </div>
      ) : null}
    </div>
  );
};

export default Radar;
